/**
 * Support hours in the visitor's own time (ADR-0065).
 *
 * The backend hands over windows of instants in UTC (Drupal resolved the
 * team's time zones, daylight saving and closed days); here they only get
 * formatted with the visitor's clock: "Mon–Fri 8:00 AM–6:00 PM".
 */

const DAY_MS = 86400000;

function startOfDay(date) {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
}

/** The windows that apply to a language (empty locales: every language). */
export function windowsFor(supportHours, locale) {
  const windows = (supportHours && supportHours.windows) || [];
  return windows.filter((w) => !w.locales || !w.locales.length || !locale || w.locales.includes(locale));
}

/**
 * "Mon–Fri 8:00 AM–6:00 PM · Sat 9:00 AM–1:00 PM": the week, in the
 * visitor's time zone; '' without windows. `words` names a whole day
 * (`allDay`: "24 hours") and the whole week (`everyDay(hours)`: "Every
 * day, …"); without them, "0:00–24:00" and "Mon–Sun".
 */
export function describeHours(supportHours, locale, uiLocale, now = new Date(), words = {}) {
  // The usual week, holidays aside (ADR-0066 §1); older backends: windows.
  const windows = windowsFor(supportHours && supportHours.typical ? { windows: supportHours.typical } : supportHours, locale);
  if (!windows.length) return '';
  const time = new Intl.DateTimeFormat(uiLocale, { hour: 'numeric', minute: '2-digit' });
  const weekday = new Intl.DateTimeFormat(uiLocale, { weekday: 'short' });
  const today = startOfDay(now).getTime();
  // Each weekday with its spans, from its next occurrence: today's hours
  // may be over already, next week's same day still has them (the backend
  // sends 8 days).
  const byWeekday = new Map();
  for (let i = 0; i < 8; i++) {
    // Daylight saving can make a day 23 or 25 hours: re-anchor at midnight.
    const from = startOfDay(new Date(today + i * DAY_MS + 3600000));
    const to = startOfDay(new Date(from.getTime() + DAY_MS + 3600000));
    const spans = windows
      .map((w) => ({ start: new Date(w.start), end: new Date(w.end) }))
      .filter((w) => w.start >= from && w.start < to)
      .sort((a, b) => a.start - b.start)
      .map((w) => {
        // A window ending at the next midnight ends at "24:00", not "0:00".
        const toMidnight = w.end - w.start >= DAY_MS - 3600000 && w.end.getHours() === 0 && w.end.getMinutes() === 0;
        if (toMidnight && w.start.getHours() === 0 && w.start.getMinutes() === 0 && words.allDay) return words.allDay;
        return `${time.format(w.start)}–${toMidnight ? '24:00' : time.format(w.end)}`;
      });
    if (spans.length && !byWeekday.has(from.getDay())) {
      byWeekday.set(from.getDay(), { date: from, label: weekday.format(from), spans: [...new Set(spans)].join(', ') });
    }
  }
  const days = [...byWeekday.values()];
  // In week order (Monday first), days with the same spans in a row merged.
  const order = (date) => (date.getDay() + 6) % 7;
  days.sort((a, b) => order(a.date) - order(b.date));
  const groups = [];
  for (const day of days) {
    const last = groups[groups.length - 1];
    if (day.spans && last && last.spans === day.spans && order(day.date) === order(last.to.date) + 1) {
      last.to = day;
    }
    else if (day.spans) {
      groups.push({ from: day, to: day, spans: day.spans });
    }
  }
  // The same hours all seven days: "Every day, 24 hours".
  if (groups.length === 1 && order(groups[0].to.date) - order(groups[0].from.date) === 6 && words.everyDay) {
    return words.everyDay(groups[0].spans);
  }
  return groups
    .map((g) => `${g.from === g.to ? g.from.label : `${g.from.label}–${g.to.label}`} ${g.spans}`)
    .join(' · ');
}

/**
 * When the next opening is, relative to today: { day: 'today' | 'tomorrow'
 * | <weekday name>, time }, or null.
 */
export function opening(iso, uiLocale, now = new Date()) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const days = Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);
  const time = new Intl.DateTimeFormat(uiLocale, { hour: 'numeric', minute: '2-digit' }).format(date);
  const day = days <= 0 ? 'today' : days === 1 ? 'tomorrow' : new Intl.DateTimeFormat(uiLocale, { weekday: 'long' }).format(date);
  return { day, time };
}
